/**
 * Entity persistence: mobs, dropped items, arrows, XP orbs and primed TNT as a
 * compact versioned JSON blob, stored per dimension in the world save
 * (world/save.ts `entities`).
 *
 * Vanilla saves entities with their chunk; here the blob is global per
 * dimension. Restored mobs are ordinary mobs again: the manager's despawn
 * rules (hostile far = instant, mid = random) apply from the next tick, and
 * mobs in not-yet-ready chunks stay frozen until the chunk loads.
 *
 * Per-type state goes through `saveExtra()` / `loadExtra()` on the mob when it
 * defines them, else through the registry below (registerMobExtra), else
 * nothing.
 */

import type { MobType, Stack } from '../game/types';
import type { EntityManager } from './manager';
import type { ItemEntity, ItemEntityManager } from './itemEntities';
import type { XpOrbManager } from './xp';
import type { Mob } from './mob';
import { Arrow } from './arrow';
import { leashes } from './leash';
import type { Charge } from '../world/tnt';

export const ENTITY_SAVE_VERSION = 1;

/**
 * [type, x, y, z, vx, vy, vz, yaw, headYaw, pitch, health, age, fireTicks, extra?, customName?, leash?, gear?, owner?]
 * gear = [heldItem, armour ids helmet..boots] when the mob carries any (rolled at spawn, so not derivable from the type).
 */
export type MobRow = [MobType, number, number, number, number, number, number, number, number, number, number, number, number, unknown?, (string | null)?, unknown?, unknown?, (string | null)?];
/** [id, count, dmg, x, y, z, vx, vy, vz, pickupDelay, age, extras?] */
export type ItemRow = [number, number, number, number, number, number, number, number, number, number, number, Partial<Stack>?];
/** [x, y, z, vx, vy, vz, stuck, fromPlayer, damage, life, stuckTicks, crit, shooterId] */
export type ArrowRow = [number, number, number, number, number, number, 0 | 1, 0 | 1, number, number, number, 0 | 1, number];
/** [x, y, z, vx, vy, vz, fuse] */
export type TntRow = [number, number, number, number, number, number, number];

export interface EntitySave {
  v: number;
  mobs: MobRow[];
  items: ItemRow[];
  arrows: ArrowRow[];
  /** XpOrbManager.serialize rows. */
  xp: number[][];
  tnt: TntRow[];
}

export interface EntityStores {
  entities: EntityManager;
  items?: ItemEntityManager;
  xp?: XpOrbManager;
  /** TntSim.charges (primed TNT). */
  tnt?: Charge[];
}

// ---------------------------------------------------------- extra registry

export interface MobExtra {
  save(m: Mob): unknown;
  load(m: Mob, data: unknown): void;
}

const extras = new Map<MobType, MobExtra>();

/** Give a mob type saved state beyond the common fields. */
export function registerMobExtra(type: MobType, extra: MobExtra): void { extras.set(type, extra); }

type Bag = Record<string, unknown>;
const bag = (m: Mob): Bag => m as unknown as Bag;
const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);

// animals (baby / love / cooldown, sheep wool, chicken egg timer) save through Animal.saveExtra
registerMobExtra('creeper', {
  // fuse: swellTicks (0..30) and whether it is still swelling
  save: (m) => [num(bag(m).swellTicks), num(bag(m).swellDir, -1)],
  load: (m, d) => {
    const a = d as number[];
    bag(m).swellTicks = num(a[0]); bag(m).swellDir = num(a[1], -1);
    m.swell = num(a[0]) / 30;
  },
});
registerMobExtra('zombified_piglin', {
  save: (m) => [num(bag(m).anger)],
  load: (m, d) => {
    const anger = num((d as number[])[0]);
    const b = bag(m);
    b.anger = anger; b.angry = anger > 0;
  },
});
registerMobExtra('magma_cube', {
  save: (m) => [num(bag(m).size, 4)],
  load: (m, d) => {
    const s = num((d as number[])[0], 4);
    const fn = bag(m).setSize;
    if (typeof fn === 'function') (fn as (s: number) => void).call(m, s === 1 || s === 2 ? s : 4);
  },
});

interface WithExtra { saveExtra?(): unknown; loadExtra?(d: unknown): void }

function saveExtra(m: Mob): unknown {
  const own = m as unknown as WithExtra;
  if (typeof own.saveExtra === 'function') return own.saveExtra();
  return extras.get(m.type)?.save(m);
}

function loadExtra(m: Mob, d: unknown): void {
  if (d === undefined || d === null) return;
  const own = m as unknown as WithExtra;
  try {
    if (typeof own.loadExtra === 'function') own.loadExtra(d);
    else extras.get(m.type)?.load(m, d);
  } catch { /* malformed extra: keep defaults */ }
}

// ------------------------------------------------------------- serialize

const r3 = (v: number): number => Math.round(v * 1000) / 1000;

/** One mob as its saved row (also the record the game server replicates to clients). */
export function serializeMob(m: Mob): MobRow {
  const row: MobRow = [m.type, r3(m.x), r3(m.y), r3(m.z), r3(m.vx), r3(m.vy), r3(m.vz),
    r3(m.yaw), r3(m.headYaw), r3(m.pitch), m.health, m.age, m.fireTicks];
  const ex = saveExtra(m);
  if (ex !== undefined) row.push(ex);
  if (m.customName) { if (row.length < 14) row.push(null); row.push(m.customName); }
  // leash (entities/leash.ts): 'p' = held by the player, [x, y, z] = knot cell; mob holders are not saved
  const lh = leashes.holderOf(m);
  if (lh && lh.kind !== 'mob') {
    while (row.length < 15) row.push(null);
    row.push(lh.kind === 'player' ? (lh.id !== undefined ? 'p:' + lh.id : 'p') : [lh.x, lh.y, lh.z]);
  }
  if (m.heldItem || m.armorPieces.length) {
    while (row.length < 16) row.push(null);
    row.push([m.heldItem, [...m.armorPieces]]);
  }
  // who tamed it (server: a player id)
  if (m.owner) {
    while (row.length < 17) row.push(null);
    row.push(m.owner);
  }
  return row;
}

/** One dropped item as its saved row. */
export function serializeItem(e: ItemEntity): ItemRow {
  const st = e.stack;
  const row: ItemRow = [st.id, st.count, st.dmg, r3(e.x), r3(e.y), r3(e.z), r3(e.vx), r3(e.vy), r3(e.vz), e.pickupDelay, e.age];
  // anything beyond id/count/dmg (enchantments, repair cost, ...) rides along as-is
  const rest: Partial<Stack> = { ...st };
  delete rest.id; delete rest.count; delete rest.dmg;
  if (Object.keys(rest).length) row.push(rest);
  return row;
}

export function serializeEntities(s: EntityStores): EntitySave {
  const mobs: MobRow[] = [];
  for (const m of s.entities.mobs) {
    if (m.removed || m.dying || !m.alive) continue;
    mobs.push(serializeMob(m));
  }
  const items: ItemRow[] = [];
  for (const e of s.items?.items ?? []) {
    if (e.collect > 0 || e.removed) continue;
    items.push(serializeItem(e));
  }
  const arrows: ArrowRow[] = [];
  for (const a of s.entities.arrows) {
    if (a.removed) continue;
    arrows.push([r3(a.x), r3(a.y), r3(a.z), r3(a.vx), r3(a.vy), r3(a.vz), a.stuck ? 1 : 0, a.fromPlayer ? 1 : 0,
      a.damage, a.life, a.stuckTicks, a.crit ? 1 : 0, a.shooterId]);
  }
  const tnt: TntRow[] = (s.tnt ?? []).map((c) => [r3(c.x), r3(c.y), r3(c.z), r3(c.vx), r3(c.vy), r3(c.vz), c.fuse]);
  return { v: ENTITY_SAVE_VERSION, mobs, items, arrows, xp: s.xp?.serialize() ?? [], tnt };
}

// ----------------------------------------------------------- deserialize

const isRow = (r: unknown, n: number): r is unknown[] => Array.isArray(r) && r.length >= n;

/**
 * Set a mob's saved state from a row of its own type (everything but the
 * type). `withLeash` also restores the leash, which lives in the module-wide
 * `leashes` registry.
 */
export function applyMobRow(em: EntityManager, m: Mob, r: readonly unknown[], withLeash = true): void {
  loadExtra(m, r[13]); // first: magma cube size resets health
  m.x = num(r[1], m.x); m.y = num(r[2], m.y); m.z = num(r[3], m.z);
  m.vx = num(r[4]); m.vy = num(r[5]); m.vz = num(r[6]);
  m.yaw = num(r[7]); m.headYaw = num(r[8]); m.pitch = num(r[9]);
  m.health = Math.max(0.5, Math.min(m.maxHealth, num(r[10], m.maxHealth)));
  m.age = num(r[11]); m.fireTicks = num(r[12]);
  if (typeof r[14] === 'string' && r[14]) { m.customName = r[14].slice(0, 50); m.persistent = true; }
  if (withLeash) {
    if (r[15] === 'p') leashes.attach(m, { kind: 'player' });
    else if (typeof r[15] === 'string' && r[15].startsWith('p:')) leashes.attach(m, { kind: 'player', id: r[15].slice(2) });
    else if (Array.isArray(r[15]) && r[15].length === 3) leashes.attach(m, { kind: 'knot', x: num(r[15][0]), y: num(r[15][1]), z: num(r[15][2]) });
  }
  m.owner = typeof r[17] === 'string' ? r[17].slice(0, 64) : '';
  const gear = r[16];
  if (Array.isArray(gear)) {
    m.heldItem = num(gear[0]);
    if (Array.isArray(gear[1])) m.armorPieces = gear[1].map((v) => num(v));
  }
  em.refreshView(m);
}

/** Build a mob from its row. `id` forces the entity id (a client mirroring the server's ids). */
export function deserializeMob(em: EntityManager, r: unknown, id?: number, withLeash = true): Mob | null {
  if (!isRow(r, 13)) return null;
  const m = em.spawnMob(r[0] as MobType, num(r[1]), num(r[2]), num(r[3]), id);
  if (!m) return null;
  m.px = m.x; m.py = m.y; m.pz = m.z;
  applyMobRow(em, m, r, withLeash);
  return m;
}

/** Drop a saved item row back into the world. */
export function deserializeItem(items: ItemEntityManager, r: unknown): ItemEntity | null {
  if (!isRow(r, 11)) return null;
  const stack: Stack = { ...((r[11] as Partial<Stack>) ?? {}), id: num(r[0]), count: num(r[1]), dmg: num(r[2]) };
  const e = items.spawn(stack, num(r[3]), num(r[4]), num(r[5]), num(r[6]), num(r[7]), num(r[8]), num(r[9]));
  if (e) e.age = num(r[10]);
  return e;
}

/** Replace the stores' contents with a saved blob. Unknown/garbled rows are skipped. */
export function deserializeEntities(s: EntityStores, data: unknown): void {
  const em = s.entities;
  leashes.clear();
  em.clear();
  s.items?.clear();
  s.xp?.load([]);
  if (s.tnt) s.tnt.length = 0;
  const d = data as Partial<EntitySave> | null;
  if (!d || typeof d !== 'object' || typeof d.v !== 'number' || d.v > ENTITY_SAVE_VERSION) return;

  for (const r of d.mobs ?? []) deserializeMob(em, r);

  if (s.items) for (const r of d.items ?? []) deserializeItem(s.items, r);

  for (const r of d.arrows ?? []) {
    if (!isRow(r, 13)) continue;
    const a = new Arrow(em.allocId(), num(r[0]), num(r[1]), num(r[2]), num(r[3]), num(r[4]), num(r[5]),
      num(r[8], 2), !!r[7], num(r[12]));
    a.life = num(r[9]); a.stuckTicks = num(r[10]); a.crit = !!r[11];
    if (r[6]) {
      a.stuck = true;
      a.view.stuck = true;
      // the cell it is lodged in (the arrow drops once that block goes)
      (a as unknown as Bag).stuckCell = [Math.floor(a.x), Math.floor(a.y), Math.floor(a.z)];
    }
    em.adoptArrow(a);
  }

  s.xp?.load(Array.isArray(d.xp) ? d.xp : []);

  if (s.tnt) {
    for (const r of d.tnt ?? []) {
      if (!isRow(r, 7)) continue;
      s.tnt.push({ x: num(r[0]), y: num(r[1]), z: num(r[2]), vx: num(r[3]), vy: num(r[4]), vz: num(r[5]), fuse: num(r[6], 80), onGround: false });
    }
  }
}
